import CustomButton from "../../components/Form/ArrowButton";
import Input from "../../components/Form/Inputfield";
import CustomDropDown from "../../components/Form/Dropdown";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import UploadIcon from "../../assets/Icons/upload.svg";
import { USER_INPUT_FIELD_TYPES } from "../../constants/userInputFieldNames";
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";
import { raiseComplaintOrder } from "../../store";
import showToast from "../../utils/toastNotifications";
import { GoSync } from "react-icons/go";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import { referencesFormats } from "../../constants/commonConstants";
import ClipLoader from "react-spinners/ClipLoader";
import CustomFileIcon from "../../components/CustomFileIcon";

const RaiseComplaintForm = () => {
  const [selectedValue, setSelectedValue] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Get the orderID and collectionId from the URL
  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get("order_Id");
  const collectionId = queryParams.get("collection_Id");

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      subject: "",
      description: "",
      contactMethod: "",
      preferedResolution: "",
    },
    {
      subject: true,
      description: true,
      contactMethod: true,
      preferedResolution: true,
    },
    {
      subject: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      description: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      contactMethod: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      preferedResolution: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  // Thunks
  const [doRaiseComplaint, isRaiseComplaintLoading, raiseComplaintError] =
    useThunk(raiseComplaintOrder);

  const [
    doUploadRefrencesToFirebase,
    isUploadRefrencesToFirebaseLoading,
    uploadRefrencesToFirebaseError,
  ] = useFirebaseFileUpload();

  // State to store the files uploaded by the user
  const [uploadFiles, setUploadFiles] = useState([]);

  //Drag Upload
  const { getRootProps, getInputProps } = useDropzone({
    accept: referencesFormats,
    onDrop: (acceptedFiles) => {
      setUploadFiles((prev) => [...prev, ...acceptedFiles]);
    },
  });

  const handleRaiseOrderComplaint = async () => {
    // Mark all fields as touched
    setTouched({
      subject: true,
      description: true,
      contactMethod: true,
      preferedResolution: true,
    });

    if (!validateForm()) {
      console.log("Validation failed");
      showToast("error", "Please fill all required fields");
      return;
    }

    // Refrences Upload
    const fileUploadResult = await doUploadRefrencesToFirebase(
      uploadFiles,
      "complaint"
    );

    if (!fileUploadResult.success) {
      showToast("error", fileUploadResult?.error?.message || "Failed to upload files");
      return;
    }

    const downloadURLs = fileUploadResult.uploadedUrls;

    // Raise the complaint
    const result = await doRaiseComplaint({
      orderId: collectionId,
      subject: formData?.subject,
      description: formData?.description,
      contactMethod: formData?.contactMethod?.toLowerCase(),
      preferedResolution: formData?.preferedResolution,
      references: downloadURLs,
    });
    if (result?.success) {
      showToast("success", "Complaint Raised successfully");
      setTimeout(() => {
        navigate("/raise-complaint");
      }, 2000);
    } else {
      console.log("Error placing order", result.error);
      showToast("error", result?.error?.message);
    }
  };

  console.log("formData", formData);

  return (
    <div className="flex flex-col flex-grow h-screen min-w-[340px] overflow-x-hidden">
      {/* header section */}
      <div className="text-primary flex flex-col sm:flex-row gap-y-4 font-roboto font-semibold leading-5 text-[24px] pb-4 lg:ml-[calc(256px+48px)] ml-[24px] lg:mt-[40px] mt-[106px]">
        <h2 className=" mr-2">Raise Complaint - Order </h2>
        <h2>{orderId}</h2>
      </div>

      {/* scrollable section */}
      <div className="overflow-y-auto ">
        <div className="bg-white   ml-6 xl:ml-[48px] mr-[24px] lg:mr-[48px] mb-[48px] border border-medium-gray rounded-lg py-8 px-[16px] lg:px-[48px] xl:px-[136px]   md:py-10  max-w-full md:max-w-[2200px] w-auto mx-auto">
          <div className="flex flex-col justify-center items-center mt-[-80px] w-full">
            <div
              className="grid
      grid-cols-1
      gap-x-6 mt-10 w-full
      justify-center items-center
    "
            >
              <div className="mt-20">
                <Input
                  label="Subject"
                  id="subject"
                  name="subject"
                  placeholder="Enter the Complaint Subject"
                  inputStyle="w-full max-h-[166px] "
                  value={formData.subject}
                  error={touched.subject && errors.subject}
                  onChange={(e) => handleInputChange("subject", e.target.value)}
                  onBlur={(e) => handleBlur("subject", e.target.value)}
                />
              </div>

              <div className="mt-6">
                <Input
                  label="Complaint Description"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  onBlur={(e) => handleBlur("description", e.target.value)}
                  error={touched.description && errors.description}
                  placeholder="Minimum 30 characters and maximum 1000 characters."
                  inputStyle="w-full max-h-[166px] h-[166px] pb-[134px] text-left placeholder:whitespace-normal sm:placeholder:text-sm"
                />
              </div>

              <div className="mt-6 w-full overflow-hidden">
                <span className="lg:text-level-3 md:text-level-3 text-secondary font-medium leading-6 w-full xl:w-[664px] sm:text-level-4">
                  Additional Documents/ Reference Materials
                </span>
                
                <div className="w-full">
                  {
                    uploadFiles?.length > 0 && 
                    <div className="flex flex-wrap  gap-2 lg:gap-4 m-2 lg:m-4">
                      {
                        uploadFiles.map((file, index) => {
                          return <CustomFileIcon 
                                    name={file.name} 
                                    extension={file.name.split('.').pop().toLowerCase()}
                                    downloadable={false}
                                    removeFile={() => setUploadFiles(prev => prev.filter((f, i) => i !== index))}
                                  />
                        })
                      }
                    </div>
                  }
                </div>

                <div
                  {...getRootProps()}
                  className="border-dashed mt-3 border-2 border-blue-shades rounded p-4 h-[192px] flex flex-col items-center justify-center cursor-pointer"
                >
                  <input {...getInputProps()} className="hidden" />
                  <div className="">
                    {isUploadRefrencesToFirebaseLoading ? (
                      <ClipLoader
                        loading={
                          isUploadRefrencesToFirebaseLoading
                        }
                        speedMultiplier={1}
                        size={22}
                        color="#60a5fa"
                      />
                    ) : (
                      <div className=" relative">
                        <img
                          src={UploadIcon}
                          className="w-[51px] h-[43px]"
                          alt="Upload"
                        />
                        <span className=" w-[22px] h-[22px] rounded-full bg-blue-400 text-[14px] font-inter text-white flex items-center justify-center absolute -top-1 -right-1">
                          {uploadFiles.length || 0}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="mb-2 text-center text-secondary font-inter leading-5 mt-6 text-level-4">
                    Drag your file(s) to start uploading{" "} - Maximum size of a file is 20MB
                  </p>
                  <p className="text-gray text-level-5  mb-[8px]">OR</p>
                  <p className="border-[1px] border-blue-shades rounded-[8px] py-[6px] px-3 text-level-5 text-blue-shades font-semibold leading-[18px] ">
                    Browse files
                  </p>
                </div>
              </div>

              <div
                className="grid
                grid-cols-1
                sm:grid-cols-2 
                md:grid-cols-2 
                lg:grid-cols-2 
                xl:grid-cols-2 
                2xl:grid-cols-2 
                gap-x-6 mt-10 w-full 
                justify-center items-center"
              >
                {/*<div className="flex flex-col sm:flex-row md:flex-row lg:flex-row mt-10">*/}
                <div className=" ">
                  <CustomDropDown
                    label="Preferred Resolution"
                    id="preferred-resolution"
                    name="preferedResolution"
                    options={[
                      { id: 1, value: "Refund" },
                      { id: 2, value: "Revision" },
                      { id: 3, value: "Explanation/Clarification" },
                      { id: 4, value: "Compensation" },
                    ]}
                    defaultOption="Preferred Resolution"
                    valueKey="value"
                    idKey="id"
                    onChange={handleInputChange}
                    error={
                      touched.preferedResolution && errors.preferedResolution
                    }
                    onBlur={handleBlur}
                    dropdownDivStyle="w-full max-h-[166px]"
                    value={formData.preferedResolution}
                  />
                </div>

                <div className="">
                  <CustomDropDown
                    label="Contact Method"
                    id="contact-method"
                    name="contactMethod"
                    options={[
                      { id: 1, value: "Email" },
                      { id: 2, value: "Phone" },
                    ]}
                    defaultOption="Preferred Contact Method"
                    valueKey="value"
                    idKey="id"
                    onChange={handleInputChange}
                    value={formData.contactMethod}
                    error={touched.contactMethod && errors.contactMethod}
                    onBlur={handleBlur}
                    dropdownDivStyle="w-full max-h-[166px]"
                  />
                </div>
              </div>

              <div className="flex justify-between mt-[16px] w-full mb-[55px]">
                <CustomButton
                  text="Back"
                  onClick={() => {navigate(-1)}}
                  width="120px"
                  height="44px"
                  iconDirection="left"
                  iconPosition="before"
                />

                <button
                  className={`flex items-center justify-center rounded-[5px] font-roboto space-x-1 text-level-3 text-white font-semibold gap-[12px] px-[30px] py-[10px] transition-all duration-200 bg-blue-shades`}
                  onClick={handleRaiseOrderComplaint}
                  disabled={
                    isRaiseComplaintLoading ||
                    isUploadRefrencesToFirebaseLoading
                  }
                >
                  {isRaiseComplaintLoading ||
                  isUploadRefrencesToFirebaseLoading ? (
                    <ClipLoader
                      loading={
                        isRaiseComplaintLoading ||
                        isUploadRefrencesToFirebaseLoading
                      }
                      speedMultiplier={1}
                      size={22}
                      color="#ffffff"
                    />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaiseComplaintForm;
